import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import * as errors from './errors'
import { getContactsForCompanyIdPreProcessed } from './companies'
import { getContactsForSupplierIdPreProcessed } from './suppliers'
import { getContactsPreProcessed } from './contacts'
import { getCompanyMSService } from './companyMSServices'
import type {
  IEntitySelectItem,
  TEntityData,
  TEntityId,
  TEntityVersion,
  TOmitNamed,
} from '~/types/models'
import type { ICase, ICaseOverview } from '~/types/case/models'
import type { ICompanyMSService } from '~/types/company/models'
import type { TContact } from '~/types/contact/models'
import { Contact } from '~/cynqueModules/dataObjects/Contact'
import { groupContactsByParent } from '~/cynqueModules/groupContactsByParent'

const apiEntityPath = '/api/cases'

/**
 * The contacts a case may be raised by, given the service it is about: the
 * service's company's contacts plus its supplier's (pass both ids).
 *
 * Whoever reported it could sit on either side. A case has no anchor choice any
 * more -- the service is the only thing that scopes this list, and it is
 * optional; with no service the form offers every contact instead (see
 * getCaseContacts below). Service Requests keep the
 * anchors: services/serviceRequests.ts.
 *
 * ⚠ THE `Contact()` RE-RUN IS THE POINT, not tidiness. The two getters above
 * enrich with `CompanyContact()` / `SupplierContact()`, whose `namePlusBiz` is
 * a DIFFERENT format from the one this picker labels by -- and
 * SupplierContact's is broken outright: `SupplierContact.ts:37` applies a unary
 * `+` to a phone string, so every supplier contact's namePlusBiz ends in the
 * literal "NaN". `Contact()` rebuilds it as "Name | parent | title", which is
 * what the Case contact field showed before it was scoped and what every other
 * contact picker shows. Same reasoning, same one-line fix, as
 * getAddableContactsForOpportunityMasterServicePreProcessed.
 *
 * Either id may be absent -- a service whose company or supplier could not be
 * read -- and an absent one contributes no contacts rather than an unfiltered
 * list.
 */
export async function getContactsForCaseServicePreProcessed(
  scope: ICaseContactScope
) {
  return (await getContactGroupsForCaseService(scope)).flat()
}

/** The ids a case's contacts are scoped by; either may be absent. */
interface ICaseContactScope {
  companyId?: TEntityId
  supplierId?: TEntityId
}

/**
 * getContactsForCaseServicePreProcessed's two lists, kept apart: the company's
 * contacts, then the supplier's.
 */
async function getContactGroupsForCaseService({
  companyId,
  supplierId,
}: ICaseContactScope): Promise<TContact[][]> {
  const [companyContacts, supplierContacts] = await Promise.all([
    companyId
      ? getContactsForCompanyIdPreProcessed(companyId)
      : Promise.resolve([]),
    supplierId
      ? getContactsForSupplierIdPreProcessed(supplierId)
      : Promise.resolve([]),
  ])
  return [
    companyContacts.map((contact) => Contact(contact)),
    supplierContacts.map((contact) => Contact(contact)),
  ]
}

/**
 * The contacts to offer for a case, from the most specific thing it names:
 *
 *  1. a SERVICE: its company's and its supplier's contacts
 *     (getContactsForCaseServicePreProcessed);
 *  2. otherwise a COMPANY -- a case naming just its company
 *     (cynque-api-server #1750): that company's contacts;
 *  3. otherwise EVERY contact.
 *
 * Shared by the two pickers that should agree on who a case concerns: the case
 * form's Contact (modules/case.ts) and Send Email's To / cc / bcc
 * (modules/caseReply.ts).
 *
 * ⚠ NOTHING TO SCOPE BY FALLS BACK TO EVERY CONTACT, and must. Scoping by
 * nothing would leave an empty picker on every case whose company and service
 * are unknown. A case always has someone who reported it, so the unscoped list
 * is the right answer.
 *
 * ⚠ The service may be `{ id, label }` and nothing else -- a value just picked
 * in a select, or a case stored by an older API release. The embedded entity
 * is used when it is there, and fetched when it is not; if it cannot be read,
 * the company decides as though there were no service.
 */
export async function getCaseContacts(about: ICaseContactsAbout) {
  const scope = await resolveCaseContactScope(about)
  return scope
    ? await getContactsForCaseServicePreProcessed(scope)
    : await getContactsPreProcessed()
}

/**
 * getCaseContacts's contacts, kept in groups for a picker that shows them
 * apart (Send Email's To / cc / bcc): the company's, then the supplier's.
 * Every contact -- the unscoped case -- is grouped the same way by whom each
 * belongs to: company, supplier, TSD (groupContactsByParent -- no lead sources). Groups may be empty.
 */
export async function getCaseContactGroups(
  about: ICaseContactsAbout
): Promise<TContact[][]> {
  const scope = await resolveCaseContactScope(about)
  if (scope) {
    return await getContactGroupsForCaseService(scope)
  }
  return groupContactsByParent((await getContactsPreProcessed()) as TContact[])
}

interface ICaseContactsAbout {
  msService?: ICompanyMSService | null
  company?: IEntitySelectItem | null
}

// What getCaseContacts scopes by -- or null, for every contact.
async function resolveCaseContactScope({
  msService,
  company,
}: ICaseContactsAbout): Promise<ICaseContactScope | null> {
  const fn = `[services/cases.ts] ${resolveCaseContactScope.name} |`
  if (msService?.id) {
    let resolved: ICompanyMSService | undefined = msService
    if (!resolved.masterService) {
      try {
        resolved = await getCompanyMSService(msService.id)
      } catch (e) {
        console.error(`${fn} could not read service '${msService.id}':`, e)
        resolved = undefined
      }
    }
    if (resolved) {
      return {
        companyId: resolved.masterService?.parent?.id,
        supplierId: resolved.supplier?.id,
      }
    }
  }
  if (company?.id) {
    return { companyId: company.id }
  }
  return null
}

export async function getCases({ apiPath = apiEntityPath } = {}) {
  const callingMn = getCases.name
  return httpGet<ICase[]>({ callingMn, apiPath })
}

export async function getCase(id: TEntityId) {
  const callingMn = getCase.name
  const apiPath = `${apiEntityPath}/${id}`
  return httpGet<ICase>({ callingMn, apiPath })
}

export async function getCaseOverview(id: TEntityId) {
  const callingMn = getCaseOverview.name
  const apiPath = `${apiEntityPath}/${id}/overview`
  return httpGet<ICaseOverview>({ callingMn, apiPath })
}

/**
 * A case names its company OR its service, never both (cynque-api-server
 * #1750): a create naming both is a 400, and a PATCH setting the company while
 * a service stays is a 409. The case form keeps the service's company in its
 * `company` field -- it is the cascade's filter -- and sends the two together
 * (`sendWith`), so here, whenever a service is set, the company goes as null.
 *
 * ⚠ IN PLACE, not on a copy. saveEntityData hands its after-request callback
 * the object it sent, and that is what the page then shows and edits; a copy
 * would leave the local case naming both while the server names one.
 *
 * Only when the payload carries either key: a PATCH that touches neither must
 * not gain them.
 */
export function normalizeCompanyOrService(entityData: Partial<ICase>) {
  if (entityData.companyMSService?.id) {
    entityData.company = null
  }
  return entityData
}

export async function createCase(entity: ICase) {
  const callingMn = createCase.name
  const apiPath = apiEntityPath
  type TCaseNew = TOmitNamed<ICase, 'hrid'>
  normalizeCompanyOrService(entity)
  return httpPost<TCaseNew>({ callingMn, apiPath, entity })
}

export async function updateCase(entityData: Partial<ICase>, id: TEntityId) {
  const callingMn = updateCase.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialICase = Partial<ICase>
  normalizeCompanyOrService(entityData)
  return httpPatchMerge<TPartialICase>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteCase(entityData: ICase) {
  const callingMn = deleteCase.name
  const apiPath = apiEntityPath
  return httpDelete<ICase>({
    callingMn,
    apiPath,
    entityData,
  })
}

/** A case named in a merge: its id and the version the client last read. */
export interface ICaseMergeParty {
  id: TEntityId
  version: TEntityVersion
}

/**
 * Merge `source` INTO `survivor` (cynque-api-server #1658):
 * POST /api/cases/{survivor-id}/merges with
 * `{ version: <survivor's>, source: { id, version } }`. Returns the survivor.
 *
 * The source's notes, documents, mail, activities and links move to the
 * survivor; the source is kept, status Merged, `mergedInto` the survivor. The
 * survivor's own fields win. One transaction, so a refusal moves nothing:
 *  - 400: source and survivor are the same case;
 *  - 404: either case is gone;
 *  - 409: either case is stale (someone edited it) or already merged.
 * caseMergeErrorMessage turns each into what to tell the user.
 *
 * Not undoable: the server keeps no record of which children moved.
 */
export async function mergeCases({
  survivor,
  source,
}: {
  survivor: ICaseMergeParty
  source: ICaseMergeParty
}) {
  const callingMn = mergeCases.name
  const apiPath = `${apiEntityPath}/${survivor.id}/merges`
  return (await httpPost({
    callingMn,
    apiPath,
    entity: {
      version: survivor.version,
      source: { id: source.id, version: source.version },
    } as unknown as TEntityData,
  })) as unknown as ICase
}

/** The title the API gives the 409 for a case that is already merged. */
export const CASE_ALREADY_MERGED_TITLE = 'Case already merged'

/**
 * What to tell the user when mergeCases throws. httpPost types EVERY 409 as
 * UpdateConflictError, so the already-merged one is told apart by its title.
 */
export function caseMergeErrorMessage(error: unknown): string {
  if (error instanceof errors.UpdateConflictError) {
    const title = (error.body as Record<string, unknown> | null)?.title
    return title === CASE_ALREADY_MERGED_TITLE
      ? 'One of these cases has already been merged. Reload the list, then try again.'
      : 'One of these cases was changed by someone else. Reload the list, then try again.'
  }
  if (error instanceof errors.APIUnhandledStatus && error.statusCode === 404) {
    return 'One of these cases no longer exists. Reload the list.'
  }
  return `Merge failed: ${
    error instanceof Error ? error.message : 'unknown error'
  }.`
}
